import { t } from "@/lib/i18n/interpolate";
import { splitSentences } from "@/lib/tool-kit/admissions/counts";
import type {
  DocBlock,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import { allocateWords } from "@/lib/tool-kit/essay-templates/research-proposal";
import {
  paragraphsOrPrompt,
  parseWholeCount,
  splitItems,
  splitLeadIn,
} from "@/lib/tool-kit/essay-templates/text";

/**
 * A scholarship essay plan: what the prompt actually asks, which of your stories answers
 * each criterion, and an outline that fits the word limit.
 *
 * ## Reading a prompt
 *
 * A prompt is read sentence by sentence. Each sentence that contains a directive verb
 * (describe, explain, reflect…) or is a question (why, how…) is one "ask", and the essay
 * has to answer every ask. The reader sees the directive's usual meaning so a "discuss"
 * is not mistaken for a "describe". This is a lookup, not an interpretation: a
 * sentence it cannot classify is shown as-is, and the writer decides.
 */

export const DIRECTIVES = [
  "describe",
  "explain",
  "discuss",
  "reflect",
  "evaluate",
  "compare",
  "analyze",
  "argue",
  "identify",
  "demonstrate",
  "justify",
  "outline",
  "summarize",
  "share",
  "why",
  "how",
  "what",
  "who",
] as const;
export type Directive = (typeof DIRECTIVES)[number];

/** Whole-word patterns. British spellings and common inflections are covered. */
const DIRECTIVE_PATTERNS: Record<Directive, RegExp> = {
  describe: /\b(?:describe|recount|narrate)\b/i,
  explain: /\b(?:explain|clarify|account for)\b/i,
  discuss: /\b(?:discuss|examine|consider)\b/i,
  reflect: /\b(?:reflect|think about|look back)\b/i,
  evaluate: /\b(?:evaluate|assess|appraise|judge)\b/i,
  compare: /\b(?:compare|contrast)\b/i,
  analyze: /\b(?:analy[sz]e|break down)\b/i,
  argue: /\b(?:argue|persuade|make the case|defend)\b/i,
  identify: /\b(?:identify|pinpoint)\b/i,
  demonstrate:
    /\b(?:demonstrate|show|illustrate|provide examples?|give examples?)\b/i,
  justify: /\b(?:justify|support your)\b/i,
  outline: /\b(?:outline|sketch|lay out)\b/i,
  summarize: /\b(?:summari[sz]e|sum up)\b/i,
  share: /\b(?:share|tell us|tell the committee)\b/i,
  why: /\bwhy\b/i,
  how: /\bhow\b/i,
  what: /\bwhat\b/i,
  who: /\b(?:who|whom|whose)\b/i,
};

export interface PromptAsk {
  sentence: string;
  directives: Directive[];
  question: boolean;
}

export interface WordLimit {
  /** Present for a range such as "300–500 words". */
  min: number | null;
  max: number;
}

export interface PromptAnalysis {
  asks: PromptAsk[];
  /** A word limit found in the prompt, or null. */
  words: WordLimit | null;
  /** A character limit found in the prompt, or null. */
  characters: number | null;
}

const WORD_RANGE = /(\d[\d,]*)\s*(?:-|–|—|to)\s*(\d[\d,]*)\s*words?\b/i;
const WORD_MAX = /(\d[\d,]*)\s*words?\b/i;
const CHARACTER_LIMIT = /(\d[\d,]*)\s*characters?\b/i;
const COMMAS = /,/g;
/** "what" and "who" only count as an ask when the sentence is a question. */
const QUESTION_ONLY: ReadonlySet<Directive> = new Set(["what", "who"]);
const SENTENCE_QUESTION = /\?["'”’)\]]*$/;

function toNumber(raw: string): number {
  return Number(raw.replace(COMMAS, ""));
}

/** A word or character limit written into the prompt. */
export function findLimits(prompt: string): {
  words: WordLimit | null;
  characters: number | null;
} {
  const range = WORD_RANGE.exec(prompt);
  const single = WORD_MAX.exec(prompt);
  const characters = CHARACTER_LIMIT.exec(prompt);
  let words: WordLimit | null = null;
  if (range) {
    words = { min: toNumber(range[1]), max: toNumber(range[2]) };
  } else if (single) {
    words = { min: null, max: toNumber(single[1]) };
  }
  return { words, characters: characters ? toNumber(characters[1]) : null };
}

/** Split a prompt into the things it asks for. */
export function analysePrompt(prompt: string): PromptAnalysis {
  const asks: PromptAsk[] = [];
  for (const sentence of splitSentences(prompt)) {
    const question = SENTENCE_QUESTION.test(sentence);
    const directives = DIRECTIVES.filter(
      (id) =>
        DIRECTIVE_PATTERNS[id].test(sentence) &&
        (question || !QUESTION_ONLY.has(id))
    );
    if (directives.length > 0 || question) {
      asks.push({ sentence, directives, question });
    }
  }
  return { asks, ...findLimits(prompt) };
}

export interface Story {
  title: string;
  shows: string;
}

/** "Led the food drive — leadership, organisation" → { title, shows }. */
export function parseStories(text: string): Story[] {
  return splitItems(text).map((line) => {
    const parts = splitLeadIn(line);
    return parts
      ? { title: parts[0], shows: parts[1] }
      : { title: line, shows: "" };
  });
}

/** Criteria as typed, one per line, without duplicates. */
export function parseCriteria(text: string): string[] {
  return [...new Set(splitItems(text))];
}

export interface CriterionRow {
  criterion: string;
  story: Story | null;
}

export interface CoverageSummary {
  rows: CriterionRow[];
  uncovered: string[];
  unusedStories: string[];
  /** Stories chosen for more than one criterion. */
  sharedStories: string[];
}

/** Which story answers which criterion, and where the gaps are. */
export function summariseCoverage(
  criteria: readonly string[],
  stories: readonly Story[],
  choices: Readonly<Record<string, string>>
): CoverageSummary {
  const rows = criteria.map((criterion) => ({
    criterion,
    story: stories.find((story) => story.title === choices[criterion]) ?? null,
  }));
  const counts = new Map<string, number>();
  for (const row of rows) {
    if (row.story) {
      counts.set(row.story.title, (counts.get(row.story.title) ?? 0) + 1);
    }
  }
  return {
    rows,
    uncovered: rows.filter((row) => !row.story).map((row) => row.criterion),
    unusedStories: stories
      .filter((story) => !counts.has(story.title))
      .map((story) => story.title),
    sharedStories: [...counts].filter(([, n]) => n > 1).map(([title]) => title),
  };
}

export const OPENING_SHARE = 10;
export const CLOSING_SHARE = 15;
export const BODY_SHARE = 100 - OPENING_SHARE - CLOSING_SHARE;
/** Below this, a body part is too short to make a point and give an example. */
export const MIN_BODY_WORDS = 100;

export interface PlanTargets {
  opening: number;
  body: number[];
  closing: number;
}

/** Word targets, in fives, for an opening, one body part per criterion, and a closing. */
export function planTargets(total: number, bodyParts: number): PlanTargets {
  const parts = Math.max(1, bodyParts);
  const shares = [
    OPENING_SHARE,
    ...Array.from({ length: parts }, () => BODY_SHARE / parts),
    CLOSING_SHARE,
  ];
  const words = allocateWords(total, shares, 5);
  return {
    opening: words[0],
    body: words.slice(1, -1),
    closing: words.at(-1) ?? 0,
  };
}

export function scholarshipWordTotal(raw: string): number | null {
  const total = parseWholeCount(raw);
  return total !== null && total >= 50 && total <= 5000 ? total : null;
}

export interface ScholarshipInput {
  name: string;
  prompt: string;
  words: string;
  criteria: string;
  stories: string;
  /** Criterion → chosen story title. */
  choices: Record<string, string>;
  hook: string;
  purpose: string;
}

export interface ScholarshipLabels {
  defaultTitle: string;
  titleFor: string;
  subtitle: string;
  subtitleWords: string;
  asksHeading: string;
  asksNone: string;
  asksHead: { sentence: string; needs: string };
  needsQuestion: string;
  directiveMeanings: Record<Directive, string>;
  mapHeading: string;
  mapHead: { criterion: string; story: string; shows: string };
  mapNoStory: string;
  mapPrompts: { criteria: string; stories: string };
  gapNote: string;
  unusedNote: string;
  sharedNote: string;
  opening: string;
  closing: string;
  bodyFor: string;
  bodyGeneric: string;
  target: string;
  crowdedNote: string;
  tips: { opening: string; body: string; closing: string };
  prompts: { opening: string; body: string; closing: string };
  yourStory: string;
  checklistHeading: string;
  checklist: readonly string[];
}

type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

function asksBlocks(
  analysis: PromptAnalysis,
  labels: ScholarshipLabels
): DocBlock[] {
  if (analysis.asks.length === 0) {
    return [{ kind: "prompt", text: labels.asksNone }];
  }
  return [
    {
      kind: "table",
      head: [labels.asksHead.sentence, labels.asksHead.needs],
      rows: analysis.asks.map((ask) => {
        const needs = ask.directives.map((id) => labels.directiveMeanings[id]);
        if (needs.length === 0 && ask.question) {
          needs.push(labels.needsQuestion);
        }
        return [ask.sentence, needs.join(" ")];
      }),
    },
  ];
}

function mapBlocks(
  coverage: CoverageSummary,
  labels: ScholarshipLabels
): DocBlock[] {
  if (coverage.rows.length === 0) {
    return [{ kind: "prompt", text: labels.mapPrompts.criteria }];
  }
  const blocks: DocBlock[] = [
    {
      kind: "table",
      head: [
        labels.mapHead.criterion,
        labels.mapHead.story,
        labels.mapHead.shows,
      ],
      rows: coverage.rows.map((row) => [
        row.criterion,
        row.story?.title ?? labels.mapNoStory,
        row.story?.shows ?? "",
      ]),
    },
  ];
  if (coverage.uncovered.length > 0) {
    blocks.push({
      kind: "guidance",
      text: t(labels.gapNote, { criteria: coverage.uncovered.join("; ") }),
    });
  }
  if (coverage.unusedStories.length > 0) {
    blocks.push({
      kind: "guidance",
      text: t(labels.unusedNote, {
        stories: coverage.unusedStories.join("; "),
      }),
    });
  }
  if (coverage.sharedStories.length > 0) {
    blocks.push({
      kind: "guidance",
      text: t(labels.sharedNote, {
        stories: coverage.sharedStories.join("; "),
      }),
    });
  }
  return blocks;
}

function outlineSections(
  input: ScholarshipInput,
  coverage: CoverageSummary,
  total: number | null,
  labels: ScholarshipLabels,
  fmt: Format
): DocSection[] {
  const parts = Math.max(1, coverage.rows.length);
  const targets = total ? planTargets(total, parts) : null;
  const target = (words: number | undefined) =>
    words === undefined ? "" : `${t(labels.target, { words: fmt(words) })} `;
  const sections: DocSection[] = [
    {
      heading: labels.opening,
      blocks: [
        {
          kind: "guidance",
          text: `${target(targets?.opening)}${labels.tips.opening}`,
        },
        ...paragraphsOrPrompt(input.hook, labels.prompts.opening),
      ],
    },
  ];
  if (coverage.rows.length === 0) {
    sections.push({
      heading: labels.bodyGeneric,
      blocks: [
        {
          kind: "guidance",
          text: `${target(targets?.body[0])}${labels.tips.body}`,
        },
        { kind: "prompt", text: labels.prompts.body },
      ],
    });
  }
  coverage.rows.forEach((row, index) => {
    const words = targets?.body[index];
    const crowded = words !== undefined && words < MIN_BODY_WORDS;
    const blocks: DocBlock[] = [
      { kind: "guidance", text: `${target(words)}${labels.tips.body}` },
    ];
    if (row.story) {
      blocks.push({
        kind: "paragraph",
        text: [
          { text: `${labels.yourStory}: `, bold: true },
          {
            text: row.story.shows
              ? `${row.story.title} — ${row.story.shows}`
              : row.story.title,
          },
        ],
      });
    } else {
      blocks.push({ kind: "prompt", text: labels.prompts.body });
    }
    if (crowded) {
      blocks.push({
        kind: "guidance",
        text: t(labels.crowdedNote, { min: fmt(MIN_BODY_WORDS) }),
      });
    }
    sections.push({
      heading: t(labels.bodyFor, { criterion: row.criterion }),
      blocks,
    });
  });
  sections.push({
    heading: labels.closing,
    blocks: [
      {
        kind: "guidance",
        text: `${target(targets?.closing)}${labels.tips.closing}`,
      },
      ...paragraphsOrPrompt(input.purpose, labels.prompts.closing),
    ],
  });
  return sections;
}

export function buildScholarshipPlan(
  input: ScholarshipInput,
  labels: ScholarshipLabels,
  fmt: Format
): DocumentModel {
  const analysis = analysePrompt(input.prompt);
  const criteria = parseCriteria(input.criteria);
  const stories = parseStories(input.stories);
  const coverage = summariseCoverage(criteria, stories, input.choices);
  const total = scholarshipWordTotal(input.words);
  const name = input.name.trim();
  return {
    title: name ? t(labels.titleFor, { name }) : labels.defaultTitle,
    subtitle: total
      ? t(labels.subtitleWords, { words: fmt(total) })
      : labels.subtitle,
    sections: [
      { heading: labels.asksHeading, blocks: asksBlocks(analysis, labels) },
      { heading: labels.mapHeading, blocks: mapBlocks(coverage, labels) },
      ...outlineSections(input, coverage, total, labels, fmt),
      {
        heading: labels.checklistHeading,
        blocks: [
          {
            kind: "checklist",
            items: labels.checklist.map((text) => ({ text })),
          },
        ],
      },
    ],
  };
}
